const DEFAULT_PLACEMENT = {
  canvasAspectRatio: 1,
  area: { x: 0.22, y: 0.24, width: 0.56, height: 0.52 },
  areaRelativeTo: 'object',
  productFit: 'contain',
  artworkFit: 'contain',
  artworkLayer: 'above-product',
  artworkOpacity: 1,
  artworkRotation: 0,
  productCompositeOperation: 'source-over',
};

const SKU_PLACEMENTS = {
  ts: { area: { x: 0.25, y: 0.18, width: 0.5, height: 0.46 } },
  cp: { area: { x: 0.23, y: 0.22, width: 0.54, height: 0.34 } },
  bg: { area: { x: 0.14, y: 0.37, width: 0.72, height: 0.42 } },
  mg: { area: { x: 0.24, y: 0.28, width: 0.52, height: 0.42 } },
  ap: { area: { x: 0.22, y: 0.28, width: 0.56, height: 0.48 } },
  mp: { area: { x: 0.1, y: 0.12, width: 0.8, height: 0.76 } },
  tb: { area: { x: 0.14, y: 0.37, width: 0.72, height: 0.42 } },
  ab: { area: { x: 0.21, y: 0.23, width: 0.58, height: 0.54 } },
  st: { area: { x: 0.03, y: 0.03, width: 0.94, height: 0.94 } },
};

const ARTWORK_DISABLED_KEYWORDS = ['sticker'];

export function isArtworkDisabledForProduct(name) {
  const value = String(name || '').toLowerCase();
  if (!value) return false;
  return ARTWORK_DISABLED_KEYWORDS.some((keyword) => value.includes(keyword));
}

function finiteNumber(value, fallback) {
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function normalizedNumber(value, fallback) {
  return Math.min(1, Math.max(0, finiteNumber(value, fallback)));
}

function readArea(source, fallback) {
  const area = source?.area || source?.print_area || source?.printArea || source || {};
  const width = normalizedNumber(area.width ?? area.w, fallback.width);
  const height = normalizedNumber(area.height ?? area.h, fallback.height);
  const x = Math.min(1 - width, normalizedNumber(area.x ?? area.left, fallback.x));
  const y = Math.min(1 - height, normalizedNumber(area.y ?? area.top, fallback.y));
  return { x, y, width, height };
}

function resolveSkuPreset(skuCode) {
  const sku = String(skuCode || '').trim().toLowerCase();
  if (!sku) return {};
  if (SKU_PLACEMENTS[sku]) return SKU_PLACEMENTS[sku];
  const matchedKey = Object.keys(SKU_PLACEMENTS)
    .filter((key) => sku.startsWith(key))
    .sort((a, b) => b.length - a.length)[0];
  return matchedKey ? SKU_PLACEMENTS[matchedKey] : {};
}

export function resolveArtworkPlacement(skuCode, apiPlacement) {
  const preset = resolveSkuPreset(skuCode);
  const base = {
    ...DEFAULT_PLACEMENT,
    ...preset,
    area: preset.area || DEFAULT_PLACEMENT.area,
  };
  if (!apiPlacement || typeof apiPlacement !== 'object') return base;

  const productFit = apiPlacement.productFit ?? apiPlacement.product_fit;
  const artworkFit = apiPlacement.artworkFit ?? apiPlacement.artwork_fit ?? apiPlacement.fit;
  const artworkLayer =
    apiPlacement.artworkLayer ?? apiPlacement.artwork_layer ?? apiPlacement.layer;
  const areaRelativeTo = apiPlacement.areaRelativeTo ?? apiPlacement.area_relative_to;

  return {
    ...base,
    canvasAspectRatio: Math.max(
      0.1,
      finiteNumber(
        apiPlacement.canvasAspectRatio ?? apiPlacement.canvas_aspect_ratio,
        base.canvasAspectRatio
      )
    ),
    area: readArea(apiPlacement, base.area),
    areaRelativeTo: areaRelativeTo === 'canvas' ? 'canvas' : base.areaRelativeTo,
    productFit:
      productFit === 'cover' || productFit === 'stretch' ? productFit : base.productFit,
    artworkFit:
      artworkFit === 'cover' || artworkFit === 'stretch' ? artworkFit : base.artworkFit,
    artworkLayer: artworkLayer === 'below-product' ? artworkLayer : base.artworkLayer,
    artworkOpacity: normalizedNumber(
      apiPlacement.artworkOpacity ?? apiPlacement.artwork_opacity ?? apiPlacement.opacity,
      base.artworkOpacity
    ),
    artworkRotation: finiteNumber(
      apiPlacement.artworkRotation ?? apiPlacement.artwork_rotation ?? apiPlacement.rotation,
      base.artworkRotation
    ),
    productCompositeOperation:
      apiPlacement.productCompositeOperation ??
      apiPlacement.product_composite_operation ??
      base.productCompositeOperation,
    templateImage:
      apiPlacement.templateImage ?? apiPlacement.template_image ?? base.templateImage,
  };
}
